import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { paymentService } from './index';

// Query Keys
export const paymentQueryKeys = {
  all: ['payments'],
  status: (orderId) => [...paymentQueryKeys.all, 'status', orderId],
};

// Queries
export const usePaymentStatus = (orderId) => {
  return useQuery({
    queryKey: paymentQueryKeys.status(orderId),
    queryFn: () => paymentService.getPaymentStatus(orderId),
    enabled: !!orderId,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
};

// Mutations
export const useCreatePaymentOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderData) => paymentService.createOrder(orderData),
    onSuccess: (data, variables) => {
      // Invalidate payment status for this order
      if (variables.orderId) {
        queryClient.invalidateQueries({ queryKey: paymentQueryKeys.status(variables.orderId) });
      }
    },
    onError: (error) => {
      console.error('Payment order creation failed:', error);
    },
  });
};

export const useVerifyPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentData) => paymentService.verifyPayment(paymentData),
    onSuccess: (data, variables) => {
      // Invalidate payment status
      if (variables.razorpay_order_id) {
        queryClient.invalidateQueries({
          queryKey: paymentQueryKeys.status(variables.razorpay_order_id),
        });
      }
    },
    onError: (error) => {
      console.error('Payment verification failed:', error);
    },
  });
};

export const useHandlePaymentSuccess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentData) => paymentService.handlePaymentSuccess(paymentData),
    onSuccess: (data, variables) => {
      // Invalidate payment status
      if (variables.orderId) {
        queryClient.invalidateQueries({ queryKey: paymentQueryKeys.status(variables.orderId) });
      }

      // Invalidate orders and transactions
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
    onError: (error) => {
      console.error('Payment success handling failed:', error);
    },
  });
};

export const useHandlePaymentFailure = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (paymentData) => paymentService.handlePaymentFailure(paymentData),
    onSuccess: (data, variables) => {
      // Invalidate payment status
      if (variables.orderId) {
        queryClient.invalidateQueries({ queryKey: paymentQueryKeys.status(variables.orderId) });
      }
    },
    onError: (error) => {
      console.error('Payment failure handling failed:', error);
    },
  });
};

// Combined hook for payment operations
export const usePaymentManagement = () => {
  const createOrderMutation = useCreatePaymentOrder();
  const verifyPaymentMutation = useVerifyPayment();
  const handleSuccessMutation = useHandlePaymentSuccess();
  const handleFailureMutation = useHandlePaymentFailure();

  return {
    // Mutations
    createOrder: createOrderMutation.mutateAsync,
    verifyPayment: verifyPaymentMutation.mutateAsync,
    handlePaymentSuccess: handleSuccessMutation.mutateAsync,
    handlePaymentFailure: handleFailureMutation.mutateAsync,

    // Mutation states
    isCreatingOrder: createOrderMutation.isPending,
    isVerifying: verifyPaymentMutation.isPending,
    isHandlingSuccess: handleSuccessMutation.isPending,
    isHandlingFailure: handleFailureMutation.isPending,

    // Errors
    createOrderError: createOrderMutation.error,
    verifyError: verifyPaymentMutation.error,
    successError: handleSuccessMutation.error,
    failureError: handleFailureMutation.error,
  };
};
